import { useMutation, useQueryClient } from '@tanstack/react-query';
import { registerGuest } from '../api/events.guest.api';
import { guestRegistrationKeys } from './events.keys';
import { saveGuestRegistration } from '../guest-registration-storage';
import type { GuestRegistrationInput } from '../types';

/**
 * Register a visitor without an account (FR-282). On success the email and
 * token are remembered for this event (FR-285); that happens here, on the
 * mutation, so it survives the modal unmounting. Storage never throws - blocked
 * storage keeps the state in memory for the page.
 *
 * Failures are not toasted: the modal is open, so it reports them inline
 * beside the form (mnr-standards, write-action feedback).
 */
export function useGuestRegistration(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GuestRegistrationInput) => registerGuest(eventId, input),
    onSuccess: ({ registrationToken }, { email }) => {
      saveGuestRegistration(eventId, { email: email.trim(), token: registrationToken });
      // The token is derived from the registration, so re-registering after a stale
      // answer was cached yields the same token; drop that answer or it would
      // clear the entry just saved.
      queryClient.removeQueries({ queryKey: guestRegistrationKeys.forEvent(eventId) });
    },
  });
}
